// Promos de venta del equipo en el detalle de «Elige el teléfono» (slot `detailPromotions`), compactas y
// en los grupos «Regalo», «Descuento», «Beneficio» y «Cupón». Grupo vacío no se pinta.
import { useMemo } from 'react';
import { Gift, Sparkles, Ticket, Zap } from 'lucide-react';
import { useCatalog } from '@/domains/sales/wizard/api/useWizard';
import {
    CompactPromotionGroup,
    CompactPromotionRow,
} from '@/domains/sales/wizard/components/CompactPromotionGroup';
import type { RenderAccountBonuses } from '@/domains/sales/wizard/promotionSlots';
import { itemDisplayName } from '@/shared/lib/format/itemDisplayName';
import { formatMXN } from '@/shared/lib/format/money';
import { useSkuSalePromotions } from '../api/useWizardPromotions';
import type { StoreSalePromotionItem } from '../api/wizardPromotionsContract';
import {
    saleBonusReach,
    splitByBenefit,
    type BenefitGroup,
    type GiftDetail,
    type PromotionPiece,
} from '../lib/skuPromotionPieces';
import { giftValueCentsOf } from '../lib/giftPanel';
import { isSureKeepOnly } from '../lib/promotionAppliesTo';
import { afterSaleText } from './AfterSaleBenefits';
import { PaymentOptionsChip } from './PaymentOptionsChip';

const GROUPS: readonly { group: BenefitGroup; label: string; icon: typeof Gift }[] = [
    { group: 'gift', label: 'Regalo', icon: Gift },
    { group: 'discount', label: 'Descuento', icon: Sparkles },
    { group: 'benefit', label: 'Beneficio', icon: Zap },
    { group: 'coupon', label: 'Cupón', icon: Ticket },
];

/** Separador antes de la opción `index`: «o» si se elige, «y» si se lleva todas; comas en medio. */
function separatorBefore(index: number, total: number, word: string, allWords: boolean): string {
    if (index === 0) return '';
    return allWords || index === total - 1 ? ` ${word} ` : ', ';
}

/** «Elige 1: Audífonos (valor $399.00) o Cargador (sin existencia)», en una línea. */
export function giftOptionsText(gift: GiftDetail, names: ReadonlyMap<string, string>): string {
    const options = gift.options
        .map((option) => ({ ...option, displayName: names.get(option.sku) ?? option.name }))
        .sort((a, b) => a.displayName.localeCompare(b.displayName, 'es-MX'));
    if (options.length === 0) return '';
    const chooses = gift.chooseCount < options.length;
    const one = gift.chooseCount === 1;
    const prefix = !chooses ? '' : one ? 'Elige 1: ' : `Elige ${String(gift.chooseCount)} de: `;
    const word = chooses ? 'o' : 'y';
    return (
        prefix +
        options
            .map(
                (option, index) =>
                    separatorBefore(index, options.length, word, chooses && one) +
                    `${option.displayName} ${
                        option.inStock
                            ? `(valor ${formatMXN(giftValueCentsOf(option) / 100)})`
                            : '(sin existencia)'
                    }`,
            )
            .join('')
    );
}

/** Qué da la promo en una línea: opciones del regalo, textos, otras palancas y condiciones. */
function pieceDetail(
    piece: PromotionPiece,
    names: ReadonlyMap<string, string>,
    reach: string | null,
): string {
    return [
        ...(piece.gift ? [giftOptionsText(piece.gift, names)] : []),
        ...piece.texts.map((text, index) =>
            piece.walletBonus && index === 0 && reach !== null ? `${text} · ${reach}` : text,
        ),
        piece.extras ?? '',
        piece.conditions ?? '',
    ]
        .filter((text) => text !== '')
        .join(' · ');
}

/** Nombre de catálogo por SKU, tomado del catálogo del wizard ya cargado. */
function useCatalogNames(sessionId: string): ReadonlyMap<string, string> {
    const { data } = useCatalog(sessionId, { cached: true });
    return useMemo(
        () => new Map((data?.items ?? []).map((item) => [item.sku, itemDisplayName(item)])),
        [data],
    );
}

export function SkuPromotionsDetail({
    sessionId,
    sku,
    renderBonuses,
    storePromotions,
    withoutSureKeep = false,
    omitPromotionIds = [],
}: {
    sessionId: string;
    sku: string;
    /** Lo que pinta el detalle mientras cargan las promos. */
    renderBonuses: RenderAccountBonuses;
    /** Promos de la tienda del banner; de ellas sale el alcance de cada bono. */
    storePromotions?: readonly StoreSalePromotionItem[] | undefined;
    /** SureKeep no financia el equipo: las promos solo SureKeep no se muestran. */
    withoutSureKeep?: boolean;
    /** Promos que ya pinta el selector «Promo del equipo»; aquí sale el resto (p. ej. los bonos). */
    omitPromotionIds?: readonly string[];
}) {
    const { data } = useSkuSalePromotions(sessionId, sku);
    const names = useCatalogNames(sessionId);
    if (!data) return renderBonuses([]);
    const omitted = new Set(omitPromotionIds);
    const promotions = data.promotions.filter(
        (p) => !omitted.has(p.promotionId) && !(withoutSureKeep && isSureKeepOnly(p)),
    );
    const pieces = splitByBenefit(promotions, afterSaleText);
    return (
        <>
            {GROUPS.map(({ group, label, icon }) => (
                <CompactPromotionGroup
                    key={group}
                    label={label}
                    icon={icon}
                    tone={group}
                    items={pieces[group].map((piece) => ({
                        key: piece.promotionId,
                        node: (
                            <CompactPromotionRow
                                icon={icon}
                                tone={group}
                                name={
                                    piece.gift?.includedInSale
                                        ? `Incluido en ${piece.name}`
                                        : piece.name
                                }
                                detail={pieceDetail(
                                    piece,
                                    names,
                                    saleBonusReach(piece.promotionId, storePromotions),
                                )}
                                aside={
                                    piece.paymentLabel && (
                                        <PaymentOptionsChip label={piece.paymentLabel} />
                                    )
                                }
                            />
                        ),
                    }))}
                />
            ))}
        </>
    );
}
